import { useState, useEffect } from 'react';

import { CMS_API_URL } from '../config/apiBase';
import adaptVariation from './adaptVariation';

/**
 * Fetch a single product by brand slug and product slug. The optional
 * `schoolSlug` + `className` pair forwards the customer's school/class
 * context as query params so the backend can apply class-level attribute
 * overrides on top of the product payload.
 *
 * The optional `variationId` (a CollectionItem id, carried by menu-listing /
 * collection-shop card links as `?variation=`) switches the backend to the
 * variation-shaped payload (the collection's value subset + tier price); the
 * response is then normalized through adaptVariation so consumers keep the
 * same internal shape. Flat responses pass through untouched — the backend
 * silently falls back to the flat payload on any stale/mismatched id.
 *
 * @param {string} brandSlug
 * @param {string} slug
 * @param {string|null} [schoolSlug]
 * @param {string|null} [className]
 * @param {string|number|null} [variationId]
 * @returns {{ data: object|null, loading: boolean, error: Error|null }}
 */
export default function useProduct(brandSlug, slug, schoolSlug = null, className = null, variationId = null) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!brandSlug || !slug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const qs = new URLSearchParams();
        if (schoolSlug)  qs.set('school', schoolSlug);
        if (className)   qs.set('class', className);
        if (variationId) qs.set('variation', String(variationId));
        const qsStr = qs.toString();
        const res = await fetch(
          `${CMS_API_URL}/api/products/${encodeURIComponent(brandSlug)}/${encodeURIComponent(slug)}${qsStr ? `?${qsStr}` : ''}`,
          { signal: controller.signal }
        );

        if (!res.ok) throw new Error(`Product API returned ${res.status}`);

        const json = await res.json();
        // Variation-shaped response (pinned_attributes present) → normalize
        // to the internal configurator shape; flat product payloads unchanged.
        const payload = json && typeof json === 'object' && 'pinned_attributes' in json
          ? adaptVariation(json)
          : json;
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brandSlug, slug, schoolSlug, className, variationId]);

  return { data, loading, error };
}
